/// <reference path="../../types/leaflet-ant-path.d.ts" />
/// <reference path="../../types/leaflet-markercluster-augment.d.ts" />
/// <reference path="../../types/leaflet-geodesic.d.ts" />
/// <reference path="../../types/leaflet-glify.d.ts" />
import type {
  GeoJsonFeatureCollection,
  MapBasemapMode,
  MapInteractionsConfig,
  MapLayer,
  MapMarker,
  MapPointCloud,
  MapPointCloudSetOptions,
  MapProvider,
  MarkerClusterOptions,
  MarkerLayerOptions,
  LatLng,
  LatLngBounds,
} from '@gpx-spa/geo-domain'
import {
  createMapInteractionController,
  type MapInteractionController,
} from '../shared/map-interaction-controller.js'
import {
  logMapInteractions,
  summarizeInteractionsConfig,
} from '@gpx-spa/geo-domain'

const LEAFLET_PROVIDER_DEBUG = 'leaflet'
import {
  bindLeafletClusterGroupInteractions,
  bindLeafletMarkerInteractions,
  bindLeafletPathInteractions,
  createLeafletSurfaceHost,
} from './leaflet-map-interactions.js'
import { getMapStyleFromFeatureProperties, lineDashPatternToPixelSegments } from '@gpx-spa/geo-domain'
import L from 'leaflet'
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import { antPath } from 'leaflet-ant-path'
import 'leaflet.geodesic'
import {
  ANT_PATH_LEAFLET_PLUGIN,
  resolveAntPathStyle,
} from '../../ant-path/index.js'
import { expandCircleFeatures } from '../../geometry/expand-circle-features.js'
import { leafletMarkerClusterOptions, resolveClusterConfig } from './leaflet-cluster-config.js'
import {
  createLeafletCanvasPointCloudLayer,
  type LeafletCanvasPointCloudLayer,
} from './leaflet-canvas-point-cloud-layer.js'
import {
  createLeafletGlifyPointCloudLayer,
  LeafletGlifyPointCloudLayer,
} from './leaflet-glify-point-cloud-layer.js'
import {
  createLeafletGlifyGeoJsonPool,
  type LeafletGlifyGeoJsonPool,
} from './leaflet-glify-geojson.js'
import { partitionGlifyGeoJsonFeatures } from './partition-glify-geojson.js'
import { isLeafletWebGlAvailable } from './leaflet-webgl-support.js'
import { cloudPointPositionKey } from '../shared/map-point-cloud-to-geojson.js'
import {
  accuracyRingsMode,
  pointsForAccuracyRings,
} from '../shared/accuracy-ring-selection.js'
import {
  createLeafletCanvasAccuracyRingLayer,
  type LeafletCanvasAccuracyRingLayer,
} from './leaflet-canvas-accuracy-ring-layer.js'
import { resolvePointCloudThinning } from '../shared/thin-point-cloud-by-min-gap.js'
import {
  collectionHasLineDashArray,
  escapeHtml,
  partitionAntPathLineFeatures,
  partitionGeodesicLineFeatures,
  partitionLeafletNativeCircles,
  pathOptionsFromMapStyle,
  styleForGeoJsonFeature,
  toLeafletBounds,
} from './leaflet-geojson-helpers.js'

type LeafletPointCloudLayer = LeafletCanvasPointCloudLayer | LeafletGlifyPointCloudLayer
import { attachMapMarkerToLeafletMarker } from './leaflet-marker-metadata.js'
import { leafletMarkerFromMapMarker } from './leaflet-domain-markers.js'
import {
  createMarkerArcConnector,
  type MarkerArcConnector,
} from './marker-arc-connector.js'
import {
  GLIFY_PANE,
  INTERACTIVE_HTML_MARKER_PANE,
  INTERACTIVE_MARKER_PANE,
  TRACK_VERTEX_UNDER_MARKERS_PANE,
} from './leaflet-constants.js'
import {
  createLeafletRenderEngine,
  type LeafletRenderEngine,
  type VectorBand,
} from './leaflet-render-engine.js'
import { createLeafletHitRouter, type LeafletHitRouter } from './leaflet-hit-router.js'

/** A raster URL template, or a layer factory for sources that need more than a URL (Google Map Tiles). */
export type LeafletBasemapSource = { rasterUrl: string } | { createLayer: () => L.TileLayer }

export type LeafletBasemapConfig = {
  streets: LeafletBasemapSource
  satellite: LeafletBasemapSource
  defaultMode?: MapBasemapMode
}

export interface LeafletMapProviderOptions {
  /**
   * Raster tile URL template (e.g. MapTiler). Use this **or** {@link LeafletMapProviderOptions.basemap}.
   */
  getRasterTileUrl?: () => string
  /**
   * Streets / satellite tile URLs for {@link MapProvider.setBasemapMode}. When set, initial tiles use
   * {@link LeafletBasemapConfig.defaultMode} (default `streets`).
   */
  basemap?: LeafletBasemapConfig
  getTileAttribution: () => string
  /**
   * When `true`, Leaflet’s attribution control is shown and tile `attribution` uses {@link LeafletMapProviderOptions.getTileAttribution}.
   * Default **`false`** (no on-map attribution). Showing attribution may be required by your tile licence — opt in explicitly.
   */
  showAttribution?: boolean
  /** Initial center if fitBounds is not called immediately. */
  defaultCenter?: LatLng
  defaultZoom?: number
  /** Extra `L.tileLayer` options (e.g. MapTiler 512px: `tileSize`, `zoomOffset`). */
  tileLayerOptions?: L.TileLayerOptions
  /**
   * When `false`, Leaflet’s default zoom control is not added (use Vue controls). Default **`true`**.
   */
  zoomControl?: boolean
  /**
   * When `true` and WebGL works, point clouds, solid lines, and polygon fills use the Leaflet
   * WebGL backend (leaflet.glify). Default **`false`**: canvas point-cloud and Path, the same path
   * as a missing WebGL context. Dashes, ant-path, and geodesic stay on canvas either way.
   */
  preferWebGL?: boolean
}

/** Leaflet provider with optional raster URL swap (same attribution string). */
export type LeafletMapProvider = MapProvider & {
  setRasterTileUrl(url: string): void
}

/** Private `L.Canvas` steps that touch the canvas context (Leaflet 1.9.x). */
type LeafletCanvasInternals = L.Renderer & { _clear?: () => void; _draw?: () => void }

const noop = () => {}

/**
 * `map.remove()`, then make the removed canvas renderers inert.
 *
 * Leaflet's synchronous canvas redraw (on view reset, pan/zoom end, or when `map.remove()` stops a
 * running `fitBounds` animation) clears `_redrawRequest` without cancelling the frame already queued,
 * so `_destroyContainer` cannot cancel it. If the map is removed before that frame runs (route change
 * right after the map painted), it redraws a renderer whose `_ctx` is gone and throws
 * `reading 'clearRect'` / `reading 'save'` (GPX-37). The frame calls `this._clear()` / `this._draw()`,
 * so instance-level no-ops turn it into a no-op without patching Leaflet globally.
 */
function removeLeafletMapSafely(target: L.Map): void {
  const renderers: LeafletCanvasInternals[] = []
  target.eachLayer((layer) => {
    if (layer instanceof L.Canvas) renderers.push(layer)
  })
  target.remove()
  for (const renderer of renderers) {
    renderer._clear = noop
    renderer._draw = noop
  }
}

/**
 * Leaflet implementation of {@link MapProvider}. The host app must import `leaflet/dist/leaflet.css`.
 * GeoJSON styling and optional popups use `properties.map` (see {@link getMapStyleFromFeatureProperties}).
 * Features with `properties.type === 'circle'` and usable `center` / `radius` (m) are drawn as `L.circle` (polygon ignored).
 * `properties.map.lineAntPath` on a `LineString` uses `leaflet-ant-path` (animated direction).
 */
export function createLeafletMapProvider(options: LeafletMapProviderOptions): LeafletMapProvider {
  if (!options.basemap && !options.getRasterTileUrl) {
    throw new Error('createLeafletMapProvider: provide getRasterTileUrl or basemap')
  }
  const defaultCenter = options.defaultCenter ?? { lat: 0, lng: 0 }
  const defaultZoom = options.defaultZoom ?? 2
  const showAttribution = options.showAttribution === true
  const showZoomControl = options.zoomControl !== false
  const preferWebGL = options.preferWebGL === true
  let glifyFailed = false

  function useGlify(): boolean {
    return preferWebGL && !glifyFailed && isLeafletWebGlAvailable()
  }

  function failOverFromGlify(error: unknown): void {
    if (glifyFailed || !map) return
    glifyFailed = true
    console.warn('[leaflet] WebGL init failed, drawing on canvas', error)
    glifyGeoJsonPool?.clearAll()
    glifyGeoJsonPool = null
    glifyGeoJsonLayerIds.clear()
    map.getPane(GLIFY_PANE)?.querySelectorAll('canvas').forEach((canvas) => canvas.remove())
    for (const [layerId, layer] of [...pointCloudLayers.entries()]) {
      if (!(layer instanceof LeafletGlifyPointCloudLayer)) continue
      map.removeLayer(layer)
      pointCloudLayers.delete(layerId)
    }
    for (const [layerId, collection] of lastGeoJsonByLayer) {
      setGeoJSONLayerImpl(layerId, collection)
    }
    for (const [layerId, cloud] of lastPointCloudByLayer) {
      setPointCloudLayerImpl(layerId, cloud, lastPointCloudOptionsByLayer.get(layerId))
    }
  }

  let basemapMode: MapBasemapMode = options.basemap?.defaultMode ?? 'streets'

  function createBaseLayer(): L.TileLayer {
    const source = options.basemap?.[basemapMode]
    if (source && 'createLayer' in source) return source.createLayer()
    const url = source ? source.rasterUrl : options.getRasterTileUrl!()
    return L.tileLayer(url, {
      attribution: showAttribution ? options.getTileAttribution() : '',
      maxZoom: 19,
      minZoom: 1,
      ...options.tileLayerOptions,
    })
  }

  let map: L.Map | null = null
  let renderEngine: LeafletRenderEngine | null = null
  let hitRouter: LeafletHitRouter | null = null
  let baseRasterLayer: L.TileLayer | null = null
  let mapClickHandler: ((e: L.LeafletMouseEvent) => void) | null = null
  let mapMoveEndHandler: (() => void) | null = null
  const geoJsonLayers = new Map<string, L.Layer>()
  /** Shared glify lines+shapes (2 WebGL contexts max) — never one context per MapLayer. */
  let glifyGeoJsonPool: LeafletGlifyGeoJsonPool | null = null
  const glifyGeoJsonLayerIds = new Set<string>()
  const pointCloudLayers = new Map<string, LeafletPointCloudLayer>()
  const pointCloudAccuracyLayers = new Map<string, LeafletCanvasAccuracyRingLayer>()
  const pointCloudAccuracySelection = new Map<string, string>()
  const markerLayerGroups = new Map<string, L.LayerGroup>()
  const htmlMarkersById = new Map<string, L.Layer>()
  const markerToLayer = new Map<string, string>()
  const clusterGroups = new Map<string, L.MarkerClusterGroup>()
  /** Accuracy rings for clustered layers — must not be inside {@link L.MarkerClusterGroup} or they get clustered too. */
  const clusterAccuracyGroups = new Map<string, L.LayerGroup>()
  /** `L.Marker` refs for clustered layers — used with `map.hasLayer(marker)` to detect unclustered visibility. */
  const clusterMarkerLeaflets = new Map<string, L.Marker>()
  const clusterMarkerStores = new Map<string, Map<string, MapMarker>>()
  const clusterLayerOptions = new Map<string, MarkerClusterOptions>()
  const markerArcConnectors = new Map<string, MarkerArcConnector>()
  const clickCbs: Array<(ev: { latlng: LatLng }) => void> = []
  const moveEndCbs: Array<() => void> = []

  let interactionController: MapInteractionController | null = null
  /** Config registered before {@link mount}; applied when the map instance is created. */
  let pendingInteractionsConfig: MapInteractionsConfig | null = null

  function bringLayerHandleToFront(layerId: string): void {
    if (!map) return
    const clusterGroup = clusterGroups.get(layerId)
    if (clusterGroup?.bringToFront) {
      clusterGroup.bringToFront()
      return
    }
    const gj = geoJsonLayers.get(layerId)
    if (gj?.bringToFront) {
      gj.bringToFront()
      return
    }
    const markerGroup = markerLayerGroups.get(layerId)
    markerGroup?.bringToFront?.()
  }

  /** GeoJSON below HTML/cluster markers so parity pins stay clickable (see maps-demo). */
  function resyncLayersForInteractions(): void {
    if (!map) return
    const ids = [...layerHandles.keys()].filter((id) => !layerHandles.get(id)?.disposed)
    const geoJsonIds = ids.filter((id) => lastGeoJsonByLayer.has(id))
    const pointCloudIds = ids.filter((id) => lastPointCloudByLayer.has(id))
    const markerIds = ids.filter(
      (id) =>
        lastMarkersByLayer.has(id) || clusterMarkerStores.has(id) || clusterGroups.has(id),
    )
    const labsHoverCardLayer = 'demo-labs-hover-card'
    const order = [
      ...geoJsonIds.filter((id) => !id.startsWith('demo-labs-')),
      ...pointCloudIds.filter((id) => !id.startsWith('demo-labs-')),
      ...geoJsonIds.filter((id) => id.startsWith('demo-labs-')),
      ...markerIds.filter((id) => !id.startsWith('demo-labs-')),
      ...markerIds.filter(
        (id) => id.startsWith('demo-labs-') && id !== labsHoverCardLayer,
      ),
      ...markerIds.filter((id) => id === labsHoverCardLayer),
    ]
    for (const layerId of order) {
      if (clusterMarkerStores.has(layerId) || clusterGroups.has(layerId)) {
        rebuildClusterLeaflet(layerId)
        bringLayerHandleToFront(layerId)
        continue
      }
      const gj = lastGeoJsonByLayer.get(layerId)
      if (gj) {
        setGeoJSONLayerImpl(layerId, gj)
        bringLayerHandleToFront(layerId)
        continue
      }
      const cloud = lastPointCloudByLayer.get(layerId)
      if (cloud) {
        setPointCloudLayerImpl(layerId, cloud)
        bringLayerHandleToFront(layerId)
        continue
      }
      const markers = lastMarkersByLayer.get(layerId)
      if (markers) {
        setMarkersImpl(layerId, markers, lastMarkerOptionsByLayer.get(layerId))
        bringLayerHandleToFront(layerId)
      }
    }
  }

  function useStaticPopups(): boolean {
    return !interactionController?.hasInteractions()
  }

  function withVectorRenderer(opts: L.PathOptions): L.PathOptions {
    const renderer = renderEngine?.vectorRenderer()
    return {
      ...opts,
      ...(renderer ? { renderer } : {}),
      interactive: opts.interactive ?? true,
    }
  }

  function bandForGeometry(type: string | undefined): VectorBand {
    if (type === 'Polygon' || type === 'MultiPolygon') return 'polygon'
    if (type === 'LineString' || type === 'MultiLineString') return 'line'
    return 'circle'
  }

  function leafletMarkerOpts(): { staticPopup: boolean; pane?: string } {
    return {
      staticPopup: useStaticPopups(),
      pane: interactionController?.hasInteractions() ? INTERACTIVE_HTML_MARKER_PANE : undefined,
    }
  }

  function leafletClusterPane(): string | undefined {
    return interactionController?.hasInteractions() ? INTERACTIVE_MARKER_PANE : undefined
  }

  let clusterLayoutDebounceTimer: ReturnType<typeof setTimeout> | null = null
  let clusterLayoutViewHandler: (() => void) | null = null

  /** `L.circle` accuracy rings keyed `${layerId}::${markerId}` — not in htmlMarkersById. */
  const accuracyCircleLayers = new Map<string, L.Circle>()
  /** Avoid redundant setStyle when cluster visibility has not changed. */
  const clusterAccuracyLastShown = new Map<string, boolean>()

  const CLUSTER_ACCURACY_STYLE_SHOW: L.PathOptions = {
    color: '#3B82F6',
    weight: 1,
    opacity: 0.3,
    fillColor: '#3B82F6',
    fillOpacity: 0.08,
  }
  const CLUSTER_ACCURACY_STYLE_HIDE: L.PathOptions = {
    opacity: 0,
    fillOpacity: 0,
    weight: 0,
  }

  function scheduleClusterLayoutSync(): void {
    if (!map) return
    if (clusterLayoutDebounceTimer != null) clearTimeout(clusterLayoutDebounceTimer)
    clusterLayoutDebounceTimer = setTimeout(() => {
      clusterLayoutDebounceTimer = null
      if (!map) return
      for (const lid of clusterAccuracyGroups.keys()) {
        syncClusterLayerAccuracyVisibility(lid)
      }
      for (const connector of markerArcConnectors.values()) {
        connector.rebuildNow()
      }
    }, 120)
  }

  function getOrCreateArcConnector(layerId: string): MarkerArcConnector {
    let connector = markerArcConnectors.get(layerId)
    if (!connector) {
      connector = createMarkerArcConnector(layerId)
      markerArcConnectors.set(layerId, connector)
    }
    return connector
  }

  function disposeArcConnector(layerId: string): void {
    const connector = markerArcConnectors.get(layerId)
    if (!connector) return
    if (map?.hasLayer(connector.getLayerGroup())) {
      map.removeLayer(connector.getLayerGroup())
    }
    connector.dispose()
    markerArcConnectors.delete(layerId)
  }

  function markerLeafletsForLayer(layerId: string): Map<string, L.Marker> {
    const leaflets = new Map<string, L.Marker>()
    for (const [markerId, layer] of markerToLayer.entries()) {
      if (layer !== layerId) continue
      const lm = htmlMarkersById.get(markerId) as L.Marker | undefined
      if (lm) leaflets.set(`${layerId}::${markerId}`, lm)
    }
    return leaflets
  }

  function syncArcConnectorForLayer(
    layerId: string,
    markers: MapMarker[],
    options?: MarkerLayerOptions,
    clusterGroup?: L.MarkerClusterGroup,
  ): void {
    if (!map) return
    getOrCreateArcConnector(layerId).sync({
      markers,
      arcOptions: options?.arc,
      clusterGroup,
      markerLeaflets: clusterGroup ? clusterMarkerLeaflets : markerLeafletsForLayer(layerId),
      interactionController,
      hitRouter,
      withVectorRenderer,
      trackLine: (path) => renderEngine?.addPath(layerId, 'line', path),
    })
  }

  /** Accuracy rings only when {@link L.Marker} is on the map (unclustered / spiderfied), per leaflet.markercluster behaviour. */
  /** Circles stay in the layer group; visibility toggled via style (cheaper than add/remove). */
  function syncClusterLayerAccuracyVisibility(layerId: string): void {
    if (!map) return
    const accGroup = clusterAccuracyGroups.get(layerId)
    if (!accGroup) return
    const prefix = `${layerId}::`
    for (const [key, circle] of accuracyCircleLayers.entries()) {
      if (!key.startsWith(prefix)) continue
      const markerId = key.slice(prefix.length)
      const marker = clusterMarkerLeaflets.get(`${layerId}::${markerId}`)
      const show = marker != null && map.hasLayer(marker)
      if (clusterAccuracyLastShown.get(key) === show) continue
      clusterAccuracyLastShown.set(key, show)
      if (!accGroup.hasLayer(circle)) accGroup.addLayer(circle)
      circle.setStyle(show ? CLUSTER_ACCURACY_STYLE_SHOW : CLUSTER_ACCURACY_STYLE_HIDE)
    }
  }

  function removeAccuracyCirclesForLayer(layerId: string): void {
    const g = markerLayerGroups.get(layerId)
    for (const [k, circle] of [...accuracyCircleLayers.entries()]) {
      if (!k.startsWith(`${layerId}::`)) continue
      if (g?.hasLayer(circle)) g.removeLayer(circle)
      accuracyCircleLayers.delete(k)
    }
  }

  function removeAllHtmlMarkersInLayer(layerId: string): void {
    removeAccuracyCirclesForLayer(layerId)
    for (const [mid, lid] of [...markerToLayer.entries()]) {
      if (lid !== layerId) continue
      const lyr = htmlMarkersById.get(mid) as L.Marker | undefined
      if (lyr) markerLayerGroups.get(layerId)?.removeLayer(lyr)
      htmlMarkersById.delete(mid)
      markerToLayer.delete(mid)
    }
    markerLayerGroups.get(layerId)?.clearLayers()
  }

  function removeClusterLeafletLayer(layerId: string): void {
    const g = clusterGroups.get(layerId)
    if (g && map) {
      map.removeLayer(g)
      g.clearLayers()
    }
    const accG = clusterAccuracyGroups.get(layerId)
    if (accG && map) {
      if (map.hasLayer(accG)) map.removeLayer(accG)
      accG.clearLayers()
    }
    clusterAccuracyGroups.delete(layerId)
    for (const k of [...clusterMarkerLeaflets.keys()]) {
      if (k.startsWith(`${layerId}::`)) {
        clusterMarkerLeaflets.delete(k)
      }
    }
    for (const k of [...accuracyCircleLayers.keys()]) {
      if (k.startsWith(`${layerId}::`)) {
        accuracyCircleLayers.delete(k)
        clusterAccuracyLastShown.delete(k)
      }
    }
    clusterGroups.delete(layerId)
    clusterMarkerStores.delete(layerId)
    clusterLayerOptions.delete(layerId)
    disposeArcConnector(layerId)
  }

  function rebuildClusterLeaflet(layerId: string): void {
    if (!map) return
    const store = clusterMarkerStores.get(layerId)
    const existing = clusterGroups.get(layerId)
    if (existing) {
      map.removeLayer(existing)
      existing.clearLayers()
    }
    const prevAcc = clusterAccuracyGroups.get(layerId)
    if (prevAcc && map) {
      if (map.hasLayer(prevAcc)) map.removeLayer(prevAcc)
      prevAcc.clearLayers()
    }
    clusterAccuracyGroups.delete(layerId)
    for (const k of [...clusterMarkerLeaflets.keys()]) {
      if (k.startsWith(`${layerId}::`)) {
        clusterMarkerLeaflets.delete(k)
      }
    }
    for (const k of [...accuracyCircleLayers.keys()]) {
      if (k.startsWith(`${layerId}::`)) {
        accuracyCircleLayers.delete(k)
        clusterAccuracyLastShown.delete(k)
      }
    }
    const opts = clusterLayerOptions.get(layerId)
    const g = L.markerClusterGroup(
      leafletMarkerClusterOptions(opts, {
        clusterPane: leafletClusterPane(),
      }),
    )
    g.on('animationend', scheduleClusterLayoutSync)
    if (interactionController?.hasInteractions() && map) {
      bindLeafletClusterGroupInteractions(layerId, g, interactionController, map)
    }
    const accGroup = L.layerGroup()
    const list = store ? [...store.values()] : []
    for (const m of list) {
      const lm = attachMapMarkerToLeafletMarker(
        leafletMarkerFromMapMarker(m, leafletMarkerOpts()),
        m,
      )
      if (interactionController?.hasInteractions()) {
        bindLeafletMarkerInteractions(layerId, lm, m, interactionController)
      }
      g.addLayer(lm)
      clusterMarkerLeaflets.set(`${layerId}::${m.id}`, lm)
      const r = m.accuracyRadiusMeters
      if (r != null && Number.isFinite(r) && r > 0) {
        const circle = L.circle(
          L.latLng(m.position.lat, m.position.lng),
          withVectorRenderer({
            radius: r,
            color: '#3B82F6',
            weight: 1,
            opacity: 0.3,
            fillColor: '#3B82F6',
            fillOpacity: 0.08,
          }),
        )
        renderEngine?.addPath(layerId, 'polygon', circle)
        accGroup.addLayer(circle)
        accuracyCircleLayers.set(`${layerId}::${m.id}`, circle)
      }
    }
    clusterGroups.set(layerId, g)
    clusterAccuracyGroups.set(layerId, accGroup)
    syncArcConnectorForLayer(
      layerId,
      list,
      lastMarkerOptionsByLayer.get(layerId),
      g,
    )
    syncLeafletLayerOnMap(layerId)
    queueMicrotask(() => {
      syncClusterLayerAccuracyVisibility(layerId)
      scheduleClusterLayoutSync()
    })
  }

  const layerHandles = new Map<string, { api: MapLayer; disposed: boolean }>()
  const layerVisible = new Map<string, boolean>()
  const lastGeoJsonByLayer = new Map<string, GeoJsonFeatureCollection>()
  const lastPointCloudByLayer = new Map<string, MapPointCloud>()
  const lastPointCloudOptionsByLayer = new Map<string, MapPointCloudSetOptions | undefined>()
  const lastMarkersByLayer = new Map<string, MapMarker[]>()
  const lastMarkerOptionsByLayer = new Map<string, MarkerLayerOptions | undefined>()

  function syncLeafletLayerOnMap(layerId: string): void {
    if (!map) return
    renderEngine?.restack()
    const vis = layerVisible.get(layerId) !== false
    const clusterGroup = clusterGroups.get(layerId)
    const clusterAcc = clusterAccuracyGroups.get(layerId)
    const arcLayer = markerArcConnectors.get(layerId)?.getLayerGroup()
    if (clusterGroup) {
      if (vis) {
        if (arcLayer && !map.hasLayer(arcLayer)) arcLayer.addTo(map)
        if (clusterAcc && !map.hasLayer(clusterAcc)) clusterAcc.addTo(map)
        if (!map.hasLayer(clusterGroup)) clusterGroup.addTo(map)
        clusterGroup.bringToFront?.()
        queueMicrotask(() => {
          syncClusterLayerAccuracyVisibility(layerId)
          scheduleClusterLayoutSync()
        })
      } else {
        if (arcLayer && map.hasLayer(arcLayer)) map.removeLayer(arcLayer)
        if (map.hasLayer(clusterGroup)) map.removeLayer(clusterGroup)
        if (clusterAcc && map.hasLayer(clusterAcc)) map.removeLayer(clusterAcc)
      }
      return
    }
    const markerGroup = markerLayerGroups.get(layerId)
    if (markerGroup) {
      if (vis) {
        if (arcLayer && !map.hasLayer(arcLayer)) arcLayer.addTo(map)
        if (!map.hasLayer(markerGroup)) markerGroup.addTo(map)
        markerGroup.bringToFront?.()
      } else {
        if (arcLayer && map.hasLayer(arcLayer)) map.removeLayer(arcLayer)
        if (map.hasLayer(markerGroup)) map.removeLayer(markerGroup)
      }
      return
    }
    const layer = geoJsonLayers.get(layerId)
    if (layer || glifyGeoJsonLayerIds.has(layerId)) {
      if (vis) {
        if (layer) renderEngine?.setLayerVisible(layerId, true)
      } else {
        if (layer) renderEngine?.setLayerVisible(layerId, false)
        clearGlifyGeoJson(layerId)
      }
      if (layer || !vis) return
    }
    const pointCloud = pointCloudLayers.get(layerId)
    if (pointCloud) {
      const rings = pointCloudAccuracyLayers.get(layerId)
      if (vis) {
        if (rings && !map.hasLayer(rings)) rings.addTo(map)
        if (!map.hasLayer(pointCloud)) pointCloud.addTo(map)
      } else {
        if (map.hasLayer(pointCloud)) map.removeLayer(pointCloud)
        if (rings && map.hasLayer(rings)) map.removeLayer(rings)
      }
    }
  }

  function stripLayerForGeoJSON(layerId: string): void {
    if (clusterGroups.has(layerId) || clusterMarkerStores.has(layerId)) {
      removeClusterLeafletLayer(layerId)
    }
    const markerGroup = markerLayerGroups.get(layerId)
    if (markerGroup && map) {
      map.removeLayer(markerGroup)
      markerGroup.clearLayers()
      markerLayerGroups.delete(layerId)
      for (const [mid, lid] of [...markerToLayer.entries()]) {
        if (lid === layerId) {
          htmlMarkersById.delete(mid)
          markerToLayer.delete(mid)
        }
      }
    }
  }

  function ensureGlifyGeoJsonPool(): LeafletGlifyGeoJsonPool | null {
    if (!map || !useGlify()) return null
    if (!glifyGeoJsonPool) {
      try {
        glifyGeoJsonPool = createLeafletGlifyGeoJsonPool({
          map,
          getInteractionController: () => interactionController,
          getHitRouter: () => hitRouter,
          onWebGlFailed: failOverFromGlify,
        })
      } catch {
        return null
      }
    }
    return glifyGeoJsonPool
  }

  function clearGlifyGeoJson(layerId: string): void {
    if (!glifyGeoJsonLayerIds.has(layerId)) return
    glifyGeoJsonPool?.clearLayer(layerId)
    glifyGeoJsonLayerIds.delete(layerId)
  }

  function stripLayerForMarkers(layerId: string): void {
    const gj = geoJsonLayers.get(layerId)
    if (gj && map) {
      map.removeLayer(gj)
      geoJsonLayers.delete(layerId)
    }
    renderEngine?.clearLayer(layerId)
    clearGlifyGeoJson(layerId)
  }

  function removePointCloudAccuracyLayer(layerId: string): void {
    const rings = pointCloudAccuracyLayers.get(layerId)
    if (rings && map) {
      if (map.hasLayer(rings)) map.removeLayer(rings)
    }
    pointCloudAccuracyLayers.delete(layerId)
  }

  function removeAllContentForLayer(layerId: string): void {
    const pc = pointCloudLayers.get(layerId)
    if (pc && map) {
      map.removeLayer(pc)
      pointCloudLayers.delete(layerId)
      lastPointCloudByLayer.delete(layerId)
    }
    removePointCloudAccuracyLayer(layerId)
    const gj = geoJsonLayers.get(layerId)
    if (gj && map) {
      map.removeLayer(gj)
      geoJsonLayers.delete(layerId)
    }
    renderEngine?.clearLayer(layerId)
    clearGlifyGeoJson(layerId)
    if (clusterGroups.has(layerId) || clusterMarkerStores.has(layerId)) {
      removeClusterLeafletLayer(layerId)
    }
    const markerGroup = markerLayerGroups.get(layerId)
    if (markerGroup && map) {
      map.removeLayer(markerGroup)
      markerGroup.clearLayers()
      markerLayerGroups.delete(layerId)
      for (const [mid, lid] of [...markerToLayer.entries()]) {
        if (lid === layerId) {
          htmlMarkersById.delete(mid)
          markerToLayer.delete(mid)
        }
      }
    }
    disposeArcConnector(layerId)
    lastMarkersByLayer.delete(layerId)
    lastMarkerOptionsByLayer.delete(layerId)
  }

  function clearLayerContentImpl(layerId: string): void {
    const pc = pointCloudLayers.get(layerId)
    if (pc && map) {
      if (map.hasLayer(pc)) map.removeLayer(pc)
      pointCloudLayers.delete(layerId)
      lastPointCloudByLayer.delete(layerId)
      removePointCloudAccuracyLayer(layerId)
      pointCloudAccuracySelection.delete(layerId)
    }
    const gj = geoJsonLayers.get(layerId)
    if (gj && map) {
      map.removeLayer(gj)
      geoJsonLayers.delete(layerId)
    }
    renderEngine?.clearLayer(layerId)
    clearGlifyGeoJson(layerId)
    if (clusterGroups.has(layerId)) {
      clusterMarkerStores.set(layerId, new Map())
      rebuildClusterLeaflet(layerId)
      return
    }
    if (clusterMarkerStores.has(layerId)) {
      removeClusterLeafletLayer(layerId)
    }
    removeAllHtmlMarkersInLayer(layerId)
    const mg = markerLayerGroups.get(layerId)
    if (mg) mg.clearLayers()
    syncArcConnectorForLayer(layerId, [], lastMarkerOptionsByLayer.get(layerId))
    syncLeafletLayerOnMap(layerId)
  }

  function setPointCloudLayerImpl(
    layerId: string,
    cloud: MapPointCloud | null,
    options?: MapPointCloudSetOptions,
  ): void {
    if (!map) return
    if (!cloud || cloud.points.length === 0) {
      const existing = pointCloudLayers.get(layerId)
      if (existing && map.hasLayer(existing)) {
        map.removeLayer(existing)
      }
      pointCloudLayers.delete(layerId)
      removePointCloudAccuracyLayer(layerId)
      pointCloudAccuracySelection.delete(layerId)
      lastPointCloudByLayer.delete(layerId)
      lastPointCloudOptionsByLayer.delete(layerId)
      return
    }
    lastPointCloudByLayer.set(layerId, cloud)
    if (options !== undefined) {
      lastPointCloudOptionsByLayer.set(layerId, options)
    } else if (!lastPointCloudOptionsByLayer.has(layerId)) {
      lastPointCloudOptionsByLayer.set(layerId, undefined)
    }
    const opts = lastPointCloudOptionsByLayer.get(layerId)
    const zoom = map.getZoom()
    const visible = {
      points: resolvePointCloudThinning(cloud.points, zoom, opts),
    }
    stripLayerForGeoJSON(layerId)
    let layer = pointCloudLayers.get(layerId)
    if (!layer) {
      const cloudOpts = {
        layerId,
        getInteractionController: () => interactionController,
        onPointHit: (point: { position: { lat: number; lng: number } }) =>
          selectPointCloudAccuracy(layerId, point.position),
        getHitRouter: () => hitRouter,
      }
      if (useGlify()) {
        try {
          layer = createLeafletGlifyPointCloudLayer(cloudOpts)
        } catch {
          layer = createLeafletCanvasPointCloudLayer(cloudOpts)
        }
      } else {
        layer = createLeafletCanvasPointCloudLayer(cloudOpts)
      }
      pointCloudLayers.set(layerId, layer)
    }
    layer.setPointCloud(visible)
    syncLeafletAccuracyRings(layerId)
    syncLeafletLayerOnMap(layerId)
  }

  function selectPointCloudAccuracy(layerId: string, position: { lat: number; lng: number }): void {
    if (accuracyRingsMode(lastPointCloudOptionsByLayer.get(layerId)) !== 'on-click') return
    pointCloudAccuracySelection.set(layerId, cloudPointPositionKey(position))
    syncLeafletAccuracyRings(layerId)
    syncLeafletLayerOnMap(layerId)
  }

  function syncLeafletAccuracyRings(layerId: string): void {
    if (!map) return
    const cloud = lastPointCloudByLayer.get(layerId)
    const opts = lastPointCloudOptionsByLayer.get(layerId)
    if (!cloud) {
      removePointCloudAccuracyLayer(layerId)
      return
    }
    const visible = resolvePointCloudThinning(cloud.points, map.getZoom(), opts)
    const rings = pointsForAccuracyRings(
      visible,
      accuracyRingsMode(opts),
      pointCloudAccuracySelection.get(layerId) ?? null,
    )
    if (rings.length === 0) {
      removePointCloudAccuracyLayer(layerId)
      return
    }
    let ringsLayer = pointCloudAccuracyLayers.get(layerId)
    if (!ringsLayer) {
      ringsLayer = createLeafletCanvasAccuracyRingLayer()
      pointCloudAccuracyLayers.set(layerId, ringsLayer)
    }
    ringsLayer.setPoints(rings)
  }

  function refreshPointCloudThinningForZoom(): void {
    if (!map) return
    for (const [layerId, cloud] of lastPointCloudByLayer) {
      if (!pointCloudLayers.has(layerId)) continue
      setPointCloudLayerImpl(layerId, cloud, lastPointCloudOptionsByLayer.get(layerId))
    }
  }

  function setGeoJSONLayerImpl(layerId: string, collection: GeoJsonFeatureCollection): void {
    if (!map) return
    lastGeoJsonByLayer.set(layerId, collection)
    stripLayerForGeoJSON(layerId)
    clearGlifyGeoJson(layerId)
    renderEngine?.clearLayer(layerId)
    const existing = geoJsonLayers.get(layerId)
    if (existing) {
      map.removeLayer(existing)
      geoJsonLayers.delete(layerId)
    }
    const { nativeCircles, rest } = partitionLeafletNativeCircles(collection)
    const expanded = expandCircleFeatures({
      type: 'FeatureCollection',
      features: rest,
    })
    const { antPathLines, rest: restAfterAnt } = partitionAntPathLineFeatures(expanded)
    const { geodesicLines, rest: restAfterGeodesic } = partitionGeodesicLineFeatures({
      type: 'FeatureCollection',
      features: restAfterAnt,
    })
    const group = L.layerGroup()

    for (const feature of geodesicLines) {
      const s = getMapStyleFromFeatureProperties(feature.properties as Record<string, unknown> | undefined)
      const coords = (feature.geometry as { type: 'LineString'; coordinates: [number, number][] })
        .coordinates
      const [[lng1, lat1], [lng2, lat2]] = coords
      const weight = s.lineWidth ?? 4
      const dash = s.lineDashArray
      const hasDash = Array.isArray(dash) && dash.length > 0
      const path = new L.Geodesic(
        [[L.latLng(lat1, lng1), L.latLng(lat2, lng2)]],
        withVectorRenderer({
          color: s.lineColor ?? '#333333',
          weight,
          opacity: s.lineOpacity ?? 1,
          steps: s.lineGeodesicSteps ?? 50,
          wrap: false,
          ...(hasDash
            ? {
                dashArray: lineDashPatternToPixelSegments(dash, weight).join(' '),
              }
            : {}),
        }),
      )
      if (useStaticPopups() && s.popup) {
        path.bindPopup(s.popupHtml ? s.popup : `<div>${escapeHtml(s.popup)}</div>`)
      }
      if (interactionController?.hasInteractions()) {
        bindLeafletPathInteractions(layerId, path, feature, interactionController, hitRouter)
      }
      renderEngine?.addPath(layerId, 'line', path)
      group.addLayer(path)
    }

    for (const feature of antPathLines) {
      const s = getMapStyleFromFeatureProperties(feature.properties as Record<string, unknown> | undefined)
      const ap = resolveAntPathStyle(s)
      const coords = (feature.geometry as { type: 'LineString'; coordinates: [number, number][] })
        .coordinates
      const latlngs = coords.map(([lng, lat]) => L.latLng(lat, lng))
      const path = antPath(
        latlngs,
        withVectorRenderer({
          color: ap.lineColor,
          weight: ap.lineWidth,
          opacity: ap.lineOpacity,
          delay: ap.cycleMs,
          pulseColor: ap.pulseColor,
          /** Match static dashed lines: default polyline simplify makes animated dashes look jagged in SVG. */
          smoothFactor: 0,
          ...ANT_PATH_LEAFLET_PLUGIN,
        }),
      )
      if (useStaticPopups() && s.popup) {
        path.bindPopup(s.popupHtml ? s.popup! : `<div>${escapeHtml(s.popup!)}</div>`)
      }
      if (interactionController?.hasInteractions()) {
        bindLeafletPathInteractions(layerId, path, feature, interactionController, hitRouter)
      }
      renderEngine?.addPath(layerId, 'line', path)
      group.addLayer(path)
    }

    const forMap = { type: 'FeatureCollection' as const, features: restAfterGeodesic }

    let legacyFeatures = forMap.features
    if (useGlify() && forMap.features.length > 0) {
      const partitioned = partitionGlifyGeoJsonFeatures(forMap.features)
      if (partitioned.glifyLines.length > 0 || partitioned.glifyPolygons.length > 0) {
        const pool = ensureGlifyGeoJsonPool()
        if (pool) {
          try {
            pool.setLayerFeatures(layerId, {
              lines: partitioned.glifyLines,
              polygons: partitioned.glifyPolygons,
            })
            glifyGeoJsonLayerIds.add(layerId)
            legacyFeatures = partitioned.legacy
          } catch {
            // Broken GL / headless stubs — keep everything on Leaflet Path.
            clearGlifyGeoJson(layerId)
            legacyFeatures = forMap.features
          }
        } else {
          legacyFeatures = forMap.features
        }
      }
    }

    const legacyCollection = {
      type: 'FeatureCollection' as const,
      features: legacyFeatures,
    }

    if (legacyCollection.features.length > 0) {
      const hasDashedLine = collectionHasLineDashArray(legacyCollection)
      const gj = L.geoJSON(legacyCollection as GeoJSON.FeatureCollection, {
        ...(hasDashedLine ? { smoothFactor: 0 } : {}),
        style: (feature) => {
          if (!feature) return {}
          return withVectorRenderer(
            styleForGeoJsonFeature(feature as GeoJsonFeatureCollection['features'][0]),
          )
        },
        pointToLayer: (feature, latlng) => {
          const s = getMapStyleFromFeatureProperties(feature.properties as Record<string, unknown> | undefined)
          const r = s.pointRadius ?? 6
          const fill = s.pointFill ?? '#888888'
          const stroke = s.pointStroke ?? '#ffffff'
          const sw = s.pointStrokeWidth ?? 2
          const opts: L.CircleMarkerOptions = withVectorRenderer({
            radius: r,
            fillColor: fill,
            color: stroke,
            weight: sw,
            opacity: 1,
            fillOpacity: 1,
          })
          return L.circleMarker(latlng, opts)
        },
        onEachFeature: (feature, lyr) => {
          const s = getMapStyleFromFeatureProperties(feature.properties as Record<string, unknown> | undefined)
          renderEngine?.addPath(layerId, bandForGeometry(feature.geometry?.type), lyr)
          if (useStaticPopups() && s.popup) {
            lyr.bindPopup(s.popupHtml ? s.popup : `<div>${escapeHtml(s.popup)}</div>`)
          }
          if (interactionController?.hasInteractions()) {
            bindLeafletPathInteractions(
              layerId,
              lyr,
              feature as GeoJsonFeatureCollection['features'][number],
              interactionController,
              hitRouter,
            )
          }
        },
      })
      group.addLayer(gj)
      if (hasDashedLine) {
        queueMicrotask(() => {
          gj.eachLayer((lyr) => {
            const path = lyr as L.Path
            if (typeof path.redraw === 'function') path.redraw()
          })
        })
      }
    }

    for (const { feature, lat, lng, radiusM } of nativeCircles) {
      const s = getMapStyleFromFeatureProperties(feature.properties as Record<string, unknown> | undefined)
      const pathOpts = pathOptionsFromMapStyle(s)
      const circle = L.circle(
        L.latLng(lat, lng),
        withVectorRenderer({
          radius: radiusM,
          ...pathOpts,
        }),
      )
      renderEngine?.addPath(layerId, 'polygon', circle)
      if (useStaticPopups() && s.popup) {
        circle.bindPopup(s.popupHtml ? s.popup : `<div>${escapeHtml(s.popup)}</div>`)
      }
      if (interactionController?.hasInteractions()) {
        bindLeafletPathInteractions(layerId, circle, feature, interactionController, hitRouter)
      }
      group.addLayer(circle)
    }

    geoJsonLayers.set(layerId, group)
    renderEngine?.setRoot(layerId, group)
    syncLeafletLayerOnMap(layerId)
  }

  function setMarkersImpl(layerId: string, markerList: MapMarker[], options?: MarkerLayerOptions): void {
    if (!map) return
    lastMarkersByLayer.set(layerId, markerList)
    lastMarkerOptionsByLayer.set(layerId, options)
    stripLayerForMarkers(layerId)
    const cc = resolveClusterConfig(options)
    if (cc !== null) {
      removeAllHtmlMarkersInLayer(layerId)
      const mg = markerLayerGroups.get(layerId)
      if (mg && map) {
        map.removeLayer(mg)
        markerLayerGroups.delete(layerId)
      }
      clusterLayerOptions.set(layerId, cc)
      clusterMarkerStores.set(layerId, new Map(markerList.map((m) => [m.id, m])))
      rebuildClusterLeaflet(layerId)
      return
    }
    if (clusterGroups.has(layerId)) {
      removeClusterLeafletLayer(layerId)
    }
    clusterMarkerStores.delete(layerId)
    clusterLayerOptions.delete(layerId)
    removeAllHtmlMarkersInLayer(layerId)
    let group = markerLayerGroups.get(layerId)
    if (!group) {
      group = L.layerGroup()
      markerLayerGroups.set(layerId, group)
    }
    for (const m of markerList) {
      const lm = attachMapMarkerToLeafletMarker(
        leafletMarkerFromMapMarker(m, leafletMarkerOpts()),
        m,
      )
      if (interactionController?.hasInteractions()) {
        bindLeafletMarkerInteractions(layerId, lm, m, interactionController)
      }
      group.addLayer(lm)
      htmlMarkersById.set(m.id, lm)
      markerToLayer.set(m.id, layerId)
      const r = m.accuracyRadiusMeters
      if (r != null && Number.isFinite(r) && r > 0) {
        const circle = L.circle(
          L.latLng(m.position.lat, m.position.lng),
          withVectorRenderer({
            radius: r,
            color: '#3B82F6',
            weight: 1,
            opacity: 0.3,
            fillColor: '#3B82F6',
            fillOpacity: 0.08,
          }),
        )
        renderEngine?.addPath(layerId, 'polygon', circle)
        group.addLayer(circle)
        accuracyCircleLayers.set(`${layerId}::${m.id}`, circle)
      }
    }
    syncArcConnectorForLayer(layerId, markerList, options)
    syncLeafletLayerOnMap(layerId)
  }

  function upsertMarkerImpl(layerId: string, marker: MapMarker, options?: MarkerLayerOptions): void {
    if (!map) return
    stripLayerForMarkers(layerId)
    const cc = resolveClusterConfig(options)
    if (cc !== null && !clusterMarkerStores.has(layerId) && !markerLayerGroups.has(layerId)) {
      clusterLayerOptions.set(layerId, cc)
      clusterMarkerStores.set(layerId, new Map([[marker.id, marker]]))
      rebuildClusterLeaflet(layerId)
      return
    }
    if (clusterMarkerStores.has(layerId)) {
      clusterMarkerStores.get(layerId)!.set(marker.id, marker)
      rebuildClusterLeaflet(layerId)
      return
    }
    const existing = htmlMarkersById.get(marker.id) as L.Marker | undefined
    if (existing) {
      const prevLayer = markerToLayer.get(marker.id)
      if (prevLayer) markerLayerGroups.get(prevLayer)?.removeLayer(existing)
      htmlMarkersById.delete(marker.id)
      markerToLayer.delete(marker.id)
    }
    const accKey = `${layerId}::${marker.id}`
    const prevCircle = accuracyCircleLayers.get(accKey)
    if (prevCircle) {
      markerLayerGroups.get(layerId)?.removeLayer(prevCircle)
      accuracyCircleLayers.delete(accKey)
    }
    let group = markerLayerGroups.get(layerId)
    if (!group) {
      group = L.layerGroup()
      markerLayerGroups.set(layerId, group)
    }
    const lm = attachMapMarkerToLeafletMarker(
      leafletMarkerFromMapMarker(marker, leafletMarkerOpts()),
      marker,
    )
    if (interactionController?.hasInteractions()) {
      bindLeafletMarkerInteractions(layerId, lm, marker, interactionController)
    }
    group.addLayer(lm)
    htmlMarkersById.set(marker.id, lm)
    markerToLayer.set(marker.id, layerId)
    const r = marker.accuracyRadiusMeters
    if (r != null && Number.isFinite(r) && r > 0) {
      const circle = L.circle(
        L.latLng(marker.position.lat, marker.position.lng),
        withVectorRenderer({
          radius: r,
          color: '#3B82F6',
          weight: 1,
          opacity: 0.3,
          fillColor: '#3B82F6',
          fillOpacity: 0.08,
        }),
      )
      renderEngine?.addPath(layerId, 'polygon', circle)
      group.addLayer(circle)
      accuracyCircleLayers.set(accKey, circle)
    }
    const stored = lastMarkersByLayer.get(layerId) ?? []
    const layerMarkers = stored.filter((m) => m.id !== marker.id)
    layerMarkers.push(marker)
    lastMarkersByLayer.set(layerId, layerMarkers)
    syncArcConnectorForLayer(layerId, layerMarkers, lastMarkerOptionsByLayer.get(layerId))
    syncLeafletLayerOnMap(layerId)
  }

  function removeMarkerImpl(layerId: string, markerId: string): void {
    if (!map) return
    if (clusterMarkerStores.has(layerId)) {
      const store = clusterMarkerStores.get(layerId)!
      store.delete(markerId)
      rebuildClusterLeaflet(layerId)
      return
    }
    const m = htmlMarkersById.get(markerId) as L.Marker | undefined
    if (!m || markerToLayer.get(markerId) !== layerId) return
    const accKey = `${layerId}::${markerId}`
    const prevCircle = accuracyCircleLayers.get(accKey)
    if (prevCircle) {
      markerLayerGroups.get(layerId)?.removeLayer(prevCircle)
      accuracyCircleLayers.delete(accKey)
    }
    markerLayerGroups.get(layerId)?.removeLayer(m)
    htmlMarkersById.delete(markerId)
    markerToLayer.delete(markerId)
    const remaining = (lastMarkersByLayer.get(layerId) ?? []).filter((item) => item.id !== markerId)
    lastMarkersByLayer.set(layerId, remaining)
    syncArcConnectorForLayer(layerId, remaining, lastMarkerOptionsByLayer.get(layerId))
    syncLeafletLayerOnMap(layerId)
  }

  function createMapLayerApi(layerId: string): MapLayer {
    const disposed = () => {
      const h = layerHandles.get(layerId)
      return !h || h.disposed
    }
    return {
      get id() {
        return layerId
      },
      setGeoJSON(collection) {
        if (disposed()) return
        setGeoJSONLayerImpl(layerId, collection)
      },
      setPointCloud(cloud, options) {
        if (disposed()) return
        setPointCloudLayerImpl(layerId, cloud, options)
      },
      setMarkers(markerList, options) {
        if (disposed()) return
        setMarkersImpl(layerId, markerList, options)
      },
      upsertMarker(marker, options) {
        if (disposed()) return
        upsertMarkerImpl(layerId, marker, options)
      },
      removeMarker(markerId) {
        if (disposed()) return
        removeMarkerImpl(layerId, markerId)
      },
      clear() {
        if (disposed()) return
        clearLayerContentImpl(layerId)
      },
      show() {
        if (disposed()) return
        layerVisible.set(layerId, true)
        const lastGj = lastGeoJsonByLayer.get(layerId)
        // Remount GeoJSON so glify layers (cleared on hide) come back with the Leaflet group.
        if (lastGj) {
          setGeoJSONLayerImpl(layerId, lastGj)
        } else {
          syncLeafletLayerOnMap(layerId)
        }
      },
      hide() {
        if (disposed()) return
        layerVisible.set(layerId, false)
        syncLeafletLayerOnMap(layerId)
      },
    }
  }

  const provider: MapProvider = {
    mount(container: HTMLElement): void {
      if (map) {
        provider.destroy()
      }
      map = L.map(container, {
        zoomControl: showZoomControl,
        attributionControl: showAttribution,
        /** Vector paths use `L.canvas` by default instead of SVG ([Map preferCanvas](https://leafletjs.com/reference.html#map-prefercanvas)). */
        preferCanvas: true,
      }).setView(L.latLng(defaultCenter.lat, defaultCenter.lng), defaultZoom)
      baseRasterLayer = createBaseLayer().addTo(map)

      mapClickHandler = (e: L.LeafletMouseEvent) => {
        const latlng = { lat: e.latlng.lat, lng: e.latlng.lng }
        for (const cb of clickCbs) {
          cb({ latlng })
        }
      }
      mapMoveEndHandler = () => {
        for (const cb of moveEndCbs) {
          cb()
        }
      }
      map.on('click', mapClickHandler)
      map.on('moveend', mapMoveEndHandler)

      clusterLayoutViewHandler = () => {
        scheduleClusterLayoutSync()
        refreshPointCloudThinningForZoom()
      }
      map.on('zoomend', clusterLayoutViewHandler)
      map.on('moveend', clusterLayoutViewHandler)

      renderEngine = createLeafletRenderEngine(map)
      hitRouter = createLeafletHitRouter((on) => {
        const container = map?.getContainer()
        if (container) container.style.cursor = on ? 'pointer' : ''
      })

      if (!map.getPane(INTERACTIVE_MARKER_PANE)) {
        map.createPane(INTERACTIVE_MARKER_PANE)
        const markerPane = map.getPane(INTERACTIVE_MARKER_PANE)
        if (markerPane) {
          markerPane.style.zIndex = '670'
          // Leaflet resets img max-width only inside `.leaflet-marker-pane`.
          // Tailwind's `img { max-width: 100% }` otherwise collapses icon images
          // in a zero-width custom pane. Shadows stay visible: they live in shadowPane.
          markerPane.classList.add('leaflet-marker-pane')
        }
      }
      if (!map.getPane(INTERACTIVE_HTML_MARKER_PANE)) {
        map.createPane(INTERACTIVE_HTML_MARKER_PANE)
        const htmlPane = map.getPane(INTERACTIVE_HTML_MARKER_PANE)
        if (htmlPane) {
          htmlPane.style.zIndex = '680'
          htmlPane.classList.add('leaflet-marker-pane')
        }
      }
      if (!map.getPane(TRACK_VERTEX_UNDER_MARKERS_PANE)) {
        map.createPane(TRACK_VERTEX_UNDER_MARKERS_PANE)
        const trackVtx = map.getPane(TRACK_VERTEX_UNDER_MARKERS_PANE)
        if (trackVtx) {
          trackVtx.style.zIndex = '655'
        }
      }

      interactionController = createMapInteractionController(
        createLeafletSurfaceHost(map),
        LEAFLET_PROVIDER_DEBUG,
      )
      interactionController.setConfig(pendingInteractionsConfig)
      logMapInteractions(LEAFLET_PROVIDER_DEBUG, 'mount', {
        appliedPending: summarizeInteractionsConfig(pendingInteractionsConfig),
      })
    },

    destroy(): void {
      if (!map) return
      if (mapClickHandler) {
        map.off('click', mapClickHandler)
        mapClickHandler = null
      }
      if (mapMoveEndHandler) {
        map.off('moveend', mapMoveEndHandler)
        mapMoveEndHandler = null
      }
      if (clusterLayoutViewHandler) {
        map.off('zoomend', clusterLayoutViewHandler)
        map.off('moveend', clusterLayoutViewHandler)
        clusterLayoutViewHandler = null
      }
      if (clusterLayoutDebounceTimer != null) {
        clearTimeout(clusterLayoutDebounceTimer)
        clusterLayoutDebounceTimer = null
      }
      glifyGeoJsonPool?.clearAll()
      glifyGeoJsonPool = null
      glifyGeoJsonLayerIds.clear()
      renderEngine?.destroy()
      renderEngine = null
      hitRouter = null
      removeLeafletMapSafely(map)
      map = null
      baseRasterLayer = null
      geoJsonLayers.clear()
      pointCloudLayers.clear()
      pointCloudAccuracyLayers.clear()
      pointCloudAccuracySelection.clear()
      markerLayerGroups.clear()
      htmlMarkersById.clear()
      markerToLayer.clear()
      clusterGroups.clear()
      clusterAccuracyGroups.clear()
      clusterMarkerLeaflets.clear()
      clusterMarkerStores.clear()
      clusterLayerOptions.clear()
      for (const connector of markerArcConnectors.values()) {
        connector.dispose()
      }
      markerArcConnectors.clear()
      accuracyCircleLayers.clear()
      clusterAccuracyLastShown.clear()
      layerHandles.clear()
      layerVisible.clear()
      lastGeoJsonByLayer.clear()
      lastPointCloudByLayer.clear()
      lastMarkersByLayer.clear()
      lastMarkerOptionsByLayer.clear()
      clickCbs.length = 0
      moveEndCbs.length = 0
      interactionController = null
    },

    setCenter(center: LatLng, zoom?: number): void {
      if (!map) return
      map.setView(L.latLng(center.lat, center.lng), zoom ?? map.getZoom())
    },

    setZoom(zoom: number): void {
      map?.setZoom(zoom)
    },

    getZoom(): number | undefined {
      return map?.getZoom()
    },

    getCenter(): LatLng | undefined {
      if (!map) return undefined
      const c = map.getCenter()
      return { lat: c.lat, lng: c.lng }
    },

    latLngToContainerPoint(latlng: LatLng): { x: number; y: number } | undefined {
      if (!map) return undefined
      const p = map.latLngToContainerPoint(L.latLng(latlng.lat, latlng.lng))
      return { x: p.x, y: p.y }
    },

    notifyContainerResize(): void {
      map?.invalidateSize()
    },

    fitBounds(bounds: LatLngBounds): void {
      map?.fitBounds(toLeafletBounds(bounds), { padding: [48, 48], maxZoom: 14 })
    },

    addLayer(layerId: string): MapLayer {
      const existing = layerHandles.get(layerId)
      if (existing && !existing.disposed) return existing.api
      layerVisible.set(layerId, true)
      const api = createMapLayerApi(layerId)
      layerHandles.set(layerId, { api, disposed: false })
      return api
    },

    getLayer(layerId: string): MapLayer | undefined {
      const h = layerHandles.get(layerId)
      return h && !h.disposed ? h.api : undefined
    },

    getLayers(): MapLayer[] {
      return [...layerHandles.values()].filter((h) => !h.disposed).map((h) => h.api)
    },

    removeLayer(layerId: string): void {
      removeAllContentForLayer(layerId)
      layerVisible.delete(layerId)
      const h = layerHandles.get(layerId)
      if (h) h.disposed = true
      layerHandles.delete(layerId)
    },

    onClick(callback: (ev: { latlng: LatLng }) => void): () => void {
      clickCbs.push(callback)
      return () => {
        const i = clickCbs.indexOf(callback)
        if (i !== -1) clickCbs.splice(i, 1)
      }
    },

    onMoveEnd(callback: () => void): () => void {
      moveEndCbs.push(callback)
      return () => {
        const i = moveEndCbs.indexOf(callback)
        if (i !== -1) moveEndCbs.splice(i, 1)
      }
    },

    getBasemapMode(): MapBasemapMode {
      return basemapMode
    },

    setBasemapMode(mode: MapBasemapMode): void {
      if (!options.basemap) return
      if (mode === basemapMode) return
      const previous = options.basemap[basemapMode]
      const next = options.basemap[mode]
      basemapMode = mode
      if (!map || !baseRasterLayer) return
      if ('rasterUrl' in previous && 'rasterUrl' in next) {
        baseRasterLayer.setUrl(next.rasterUrl)
        return
      }
      baseRasterLayer.remove()
      baseRasterLayer = createBaseLayer().addTo(map)
    },

    setInteractions(config: MapInteractionsConfig | null): void {
      pendingInteractionsConfig = config
      if (!interactionController && map) {
        interactionController = createMapInteractionController(
          createLeafletSurfaceHost(map),
          LEAFLET_PROVIDER_DEBUG,
        )
      }
      interactionController?.setConfig(config)
      logMapInteractions(LEAFLET_PROVIDER_DEBUG, 'setInteractions', {
        ...summarizeInteractionsConfig(config),
        mapMounted: Boolean(map),
        controllerReady: Boolean(interactionController),
        layerCount: layerHandles.size,
      })
      if (!map || !interactionController) return
      logMapInteractions(LEAFLET_PROVIDER_DEBUG, 'resyncLayers', { layerCount: layerHandles.size })
      resyncLayersForInteractions()
    },
  }

  return Object.assign(provider, {
    setRasterTileUrl(url: string) {
      baseRasterLayer?.setUrl(url)
    },
  })
}
